import { OrderBookData } from './trading-data.service';
import { AggregatedMarketStatsQuery, AggregatedMarketStatsResponse, CandlesQuery, CandlesResponse, CreateOrderRequest, EngineStatusResponse, KlineDataResponse, KlinesData, MarketPairsData, MarketStatsResponse, OrderBookResponse, PaginatedResponse, TradingPair, UserOrdersQuery, UserTradesQuery } from '../types/cex-trade';
import { OHLCBlock } from '../types/market.types';
import { OrderResponse } from '../types/order.types';
import { cexApi, publicApi } from './api.service';
import { v4 as uuidv4 } from 'uuid';

// ================= REST API Endpoints ================= //

/**
 * Get all trading pairs
 */
export const getTradingPairs = async (params?: {
  status?: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED';
  baseAsset?: string;
  quoteAsset?: string;
}): Promise<MarketPairsData[]> => {
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.append('status', params.status);
  if (params?.baseAsset) queryParams.append('baseAsset', params.baseAsset);
  if (params?.quoteAsset) queryParams.append('quoteAsset', params.quoteAsset);

  const queryString = queryParams.toString();
  const url = `/api/v1/cex/trading-pairs${queryString ? `?${queryString}` : ''}`;

  const response = await cexApi.get<MarketPairsData[]>(url);
  return response.data;
};

/**
 * Get trading pair by symbol
 */
export const getTradingPair = async (symbol: string): Promise<TradingPair> => {
  const response = await cexApi.get<TradingPair>(`/api/v1/cex/trading-pairs/${symbol}`);
  return response.data;
};

export const orderBookKucoinToCex = (data: OrderBookResponse, symbol: string): OrderBookData => {
  const orderBookData: OrderBookData = {
    symbol: symbol,
    asks:
      data.asks?.map((ask: string[]) => ({
        price: ask[0],
        size: ask[1],
      })) || [],
    bids:
      data.bids?.map((bid: string[]) => ({
        price: bid[0],
        size: bid[1],
      })) || [],
    timestamp: data.timestamp || Date.now(),
    sequence: '',
  };
  return orderBookData;
};

/**
 * Get order book
 */
export const getOrderBook = async (symbol: string, depth: number = 20): Promise<OrderBookData> => {
  const response = await cexApi.get<OrderBookResponse>(`/api/v1/cex/orderbook/${symbol}?depth=${depth}`);
  // return response.data;
  const apiData = response.data;
  return orderBookKucoinToCex(apiData, symbol);
};

/**
 * Get market statistics
 */
export const getMarketStats = async (symbol: string): Promise<MarketStatsResponse> => {
  const response = await cexApi.get<MarketStatsResponse>(`/api/v1/cex/market-stats/${symbol}`);
  return response.data;
};

export const klineDataResponseToOHLCData = (data: KlineDataResponse): OHLCBlock => {
  return {
    time: new Date(data.closeTime).getTime(),
    open: parseFloat(data.open),
    close: parseFloat(data.close),
    high: parseFloat(data.high),
    low: parseFloat(data.low),
    volume: parseFloat(data.volume),
  };
};

export const klineDataToOHLCData = (data: KlinesData): OHLCBlock => {
  return {
    time: parseInt(data.candles[0]),
    open: parseFloat(data.candles[1]),
    close: parseFloat(data.candles[2]),
    high: parseFloat(data.candles[3]),
    low: parseFloat(data.candles[4]),
    volume: parseFloat(data.candles[5]),
  };
};

/**
 * Get K-line data
 */
export const getKlineData = async (
  symbol: string,
  interval: string,
  options?: {
    startTime?: number;
    endTime?: number;
    limit?: number;
  }
): Promise<KlineDataResponse[]> => {
  const params = new URLSearchParams();
  params.append('interval', interval);
  if (options?.startTime) params.append('startTime', options.startTime.toString());
  if (options?.endTime) params.append('endTime', options.endTime.toString());
  if (options?.limit) params.append('limit', options.limit.toString());

  const response = await cexApi.get<KlineDataResponse[]>(`/api/v1/cex/kline/${symbol}?${params.toString()}`);
  return response.data;
};

/**
 * Get recent trades
 */
export const getRecentTrades = async (symbol: string, limit: number = 50): Promise<any[]> => {
  const response = await cexApi.get<any[]>(`/api/v1/cex/recent-trades/${symbol}?limit=${limit}`);
  return response.data;
};

/**
 * Get engine status
 */
export const getEngineStatus = async (): Promise<EngineStatusResponse> => {
  const response = await cexApi.get<EngineStatusResponse>('/api/v1/cex/engine/status');
  return response.data;
};

/**
 * Get candles data from InfluxDB
 */
export const getCandlesFromInflux = async (
  symbol: string,
  query: CandlesQuery
): Promise<CandlesResponse> => {
  const params = new URLSearchParams();
  params.append('interval', query.interval);
  if (query.startTime) params.append('startTime', query.startTime.toString());
  if (query.endTime) params.append('endTime', query.endTime.toString());
  if (query.limit) params.append('limit', query.limit.toString());

  const response = await cexApi.get<CandlesResponse>(`/api/v1/cex/candles/${symbol}?${params.toString()}`);
  return response.data;
};

/**
 * Get aggregated market stats from InfluxDB
 */
export const getAggregatedMarketStats = async (
  symbol: string,
  query?: AggregatedMarketStatsQuery
): Promise<AggregatedMarketStatsResponse> => {
  const params = new URLSearchParams();
  if (query?.startTime) params.append('startTime', query.startTime.toString());
  if (query?.endTime) params.append('endTime', query.endTime.toString());

  const queryString = params.toString();
  const url = `/api/v1/cex/aggregated-market-stats/${symbol}${queryString ? `?${queryString}` : ''}`;

  const response = await cexApi.get<AggregatedMarketStatsResponse>(url);
  return response.data;
};

/**
 * Place a new order (requires authentication)
 */
export const placeOrder = async (orderData: CreateOrderRequest): Promise<OrderResponse> => {
  const response = await cexApi.post<OrderResponse>('/api/v1/cex/orders', orderData);
  return response.data;
};

/**
 * Cancel an order (requires authentication)
 */
export const cancelOrder = async (orderId: string): Promise<any> => {
  const response = await cexApi.delete(`/api/v1/cex/orders/${orderId}`);
  return response.data;
};

/**
 * Get user orders (requires authentication)
 */
export const getUserOrders = async (query?: UserOrdersQuery): Promise<OrderResponse[]> => {
  const params = new URLSearchParams();
  if (query?.symbol) params.append('symbol', query.symbol);
  if (query?.status) params.append('status', query.status);
  if (query?.side) params.append('side', query.side);
  if (query?.type) params.append('type', query.type);
  if (query?.limit) params.append('limit', query.limit.toString());
  if (query?.offset) params.append('offset', query.offset.toString());

  const queryString = params.toString();
  const url = `/api/v1/cex/user/orders${queryString ? `?${queryString}` : ''}`;

  const response = await cexApi.get<OrderResponse[]>(url);
  return response.data;
};

/**
 * Get user open orders (requires authentication)
 */
export const getUserOpenOrders = async (query?: Omit<UserOrdersQuery, 'status'>): Promise<OrderResponse[]> => {
  const params = new URLSearchParams();
  if (query?.symbol) params.append('symbol', query.symbol);
  if (query?.side) params.append('side', query.side);
  if (query?.type) params.append('type', query.type);
  if (query?.limit) params.append('limit', query.limit.toString());
  if (query?.offset) params.append('offset', query.offset.toString());

  const queryString = params.toString();
  const url = `/api/v1/cex/user/open-orders${queryString ? `?${queryString}` : ''}`;

  const response = await cexApi.get<OrderResponse[]>(url);
  return response.data;
};

/**
 * Get user trades (requires authentication)
 */
export const getUserTrades = async (query?: UserTradesQuery): Promise<PaginatedResponse<any>> => {
  const params = new URLSearchParams();
  if (query?.symbol) params.append('symbol', query.symbol);
  if (query?.side) params.append('side', query.side);
  if (query?.limit) params.append('limit', query.limit.toString());
  if (query?.offset) params.append('offset', query.offset.toString());
  if (query?.startTime) params.append('startTime', query.startTime.toString());
  if (query?.endTime) params.append('endTime', query.endTime.toString());

  const queryString = params.toString();
  const url = `/api/v1/cex/user/trades${queryString ? `?${queryString}` : ''}`;

  const response = await cexApi.get<PaginatedResponse<any>>(url);
  return response.data;
};

// ================= Admin Endpoints (requires admin authentication) ================= //

/**
 * Create trading pair (admin only)
 */
export const createTradingPair = async (tradingPairData: FormData): Promise<TradingPair> => {
  const response = await cexApi.post<TradingPair>('/api/v1/cex/admin/trading-pairs', tradingPairData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * Update trading pair (admin only)
 */
export const updateTradingPair = async (symbol: string, tradingPairData: FormData): Promise<TradingPair> => {
  const response = await cexApi.put<TradingPair>(`/api/v1/cex/admin/trading-pairs/${symbol}`, tradingPairData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * Delete trading pair (admin only)
 */
export const deleteTradingPair = async (symbol: string): Promise<void> => {
  await cexApi.delete(`/api/v1/cex/admin/trading-pairs/${symbol}`);
};

/**
 * Get engine status (admin only)
 */
export const getAdminEngineStatus = async (): Promise<EngineStatusResponse> => {
  const response = await cexApi.get<EngineStatusResponse>('/api/v1/cex/admin/engine/status');
  return response.data;
};

/**
 * Suspend market (admin only)
 */
export const suspendMarket = async (symbol: string): Promise<any> => {
  const response = await cexApi.post(`/api/v1/cex/admin/markets/${symbol}/suspend`);
  return response.data;
};

/**
 * Activate market (admin only)
 */
export const activateMarket = async (symbol: string): Promise<any> => {
  const response = await cexApi.post(`/api/v1/cex/admin/markets/${symbol}/activate`);
  return response.data;
};

/**
 * Deactivate market (admin only)
 */
export const deactivateMarket = async (symbol: string): Promise<any> => {
  const response = await cexApi.post(`/api/v1/cex/admin/markets/${symbol}/deactivate`);
  return response.data;
};

/**
 * Get user orders by user ID (admin only)
 */
export const getUserOrdersByAdmin = async (
  userId: string,
  query?: UserOrdersQuery
): Promise<OrderResponse[]> => {
  const params = new URLSearchParams();
  if (query?.symbol) params.append('symbol', query.symbol);
  if (query?.status) params.append('status', query.status);
  if (query?.side) params.append('side', query.side);
  if (query?.type) params.append('type', query.type);
  if (query?.limit) params.append('limit', query.limit.toString());
  if (query?.offset) params.append('offset', query.offset.toString());

  const queryString = params.toString();
  const url = `/api/v1/cex/admin/users/${userId}/orders${queryString ? `?${queryString}` : ''}`;

  const response = await cexApi.get<OrderResponse[]>(url);
  return response.data;
};

/**
 * Get user trades by user ID (admin only)
 */
export const getUserTradesByAdmin = async (
  userId: string,
  query?: UserTradesQuery
): Promise<PaginatedResponse<any>> => {
  const params = new URLSearchParams();
  if (query?.symbol) params.append('symbol', query.symbol);
  if (query?.side) params.append('side', query.side);
  if (query?.limit) params.append('limit', query.limit.toString());
  if (query?.offset) params.append('offset', query.offset.toString());
  if (query?.startTime) params.append('startTime', query.startTime.toString());
  if (query?.endTime) params.append('endTime', query.endTime.toString());

  const queryString = params.toString();
  const url = `/api/v1/cex/admin/users/${userId}/trades${queryString ? `?${queryString}` : ''}`;

  const response = await cexApi.get<PaginatedResponse<any>>(url);
  return response.data;
};

// ================= Error Handling ================= //

export class CexEngineError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode: number,
    public details?: any
  ) {
    super(message);
    this.name = 'CexEngineError';
  }
}

export const handleCexEngineError = (error: any): CexEngineError => {
  if (error.response) {
    const { data, status } = error.response;
    return new CexEngineError(
      data.message || 'Unknown error occurred',
      data.code || 'UNKNOWN_ERROR',
      status,
      data.details
    );
  } else if (error.request) {
    return new CexEngineError(
      'Network error - no response received',
      'NETWORK_ERROR',
      0
    );
  } else {
    return new CexEngineError(
      error.message || 'Unknown error occurred',
      'UNKNOWN_ERROR',
      500
    );
  }
};
